import type { NativePointer } from "../native/pointer";
import type { NativeSession } from "../native/session";

/** The original corner-drop interaction. The pointer keeps its transaction open
 * until the modal resolves, so cancel and Undo restore the pre-drag document. */
export class NativeObjectTrash {
  private zone = document.createElement("div");
  private dialog = document.createElement("dialog");
  private armed = false;
  private pending: {
    ids: string[];
    generation: number;
    revision: number;
  } | null = null;
  constructor(
    root: HTMLElement,
    private session: NativeSession,
    pointer: NativePointer,
    private changed: () => void,
  ) {
    this.zone.id = "object-trash-dropzone";
    this.zone.className = "object-trash-dropzone";
    this.zone.hidden = true;
    this.zone.setAttribute("aria-hidden", "true");
    this.zone.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 21c-1.1 0-2-.9-2-2V8h14v11c0 1.1-.9 2-2 2H7zM9 4h6l1 2h4v2H4V6h4l1-2zm0 7v7h2v-7H9zm4 0v7h2v-7h-2z"/></svg>';
    this.dialog.id = "object-trash-confirm";
    this.dialog.className = "object-trash-confirm";
    this.dialog.setAttribute("aria-labelledby", "object-trash-confirm-title");
    this.dialog.innerHTML =
      '<strong id="object-trash-confirm-title">Точно удалить?</strong><div><button type="button" id="object-trash-cancel" autofocus>Нет</button><button type="button" id="object-trash-accept" class="is-danger">Да</button></div>';
    root.append(this.zone, this.dialog);
    // Touch drops already suppress compatibility mouse events on the canvas.
    // Resolve a primary tap directly, without waiting for a synthetic click.
    for (const button of this.dialog.querySelectorAll<HTMLButtonElement>(
      "button",
    )) {
      let touch: number | null = null;
      button.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "touch" && e.isPrimary) touch = e.pointerId;
      });
      button.addEventListener("pointercancel", () => {
        touch = null;
      });
      button.addEventListener("pointerup", (e) => {
        if (touch !== e.pointerId) return;
        touch = null;
        const r = button.getBoundingClientRect();
        if (
          e.clientX < r.left ||
          e.clientX > r.right ||
          e.clientY < r.top ||
          e.clientY > r.bottom
        )
          return;
        e.preventDefault();
        this.resolve(button.id === "object-trash-accept");
      });
    }
    this.dialog
      .querySelector("#object-trash-cancel")!
      .addEventListener("click", () => this.resolve(false));
    this.dialog
      .querySelector("#object-trash-accept")!
      .addEventListener("click", () => this.resolve(true));
    this.dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.resolve(false);
    });
    this.dialog.addEventListener("close", () => this.resolve(false));
    pointer.moveDrop = {
      update: ({ x, y }) => {
        const threshold = Math.min(142, Math.max(108, innerWidth * 0.18));
        this.armed =
          x >= innerWidth - threshold &&
          x <= innerWidth &&
          y >= 0 &&
          y <= threshold;
        this.zone.hidden = false;
        this.zone.classList.toggle("is-armed", this.armed);
        this.zone.style.setProperty("--trash-size", threshold + "px");
      },
      drop: () => {
        const armed = this.armed;
        this.reset();
        if (!armed) return false;
        this.pending = {
          ids: session.selectedIds.slice(),
          generation: session.generation,
          revision: session.revision,
        };
        // Finish native touch/capture dispatch before changing the top layer.
        requestAnimationFrame(() => {
          if (this.pending) this.dialog.showModal();
        });
        return true;
      },
      cancel: () => this.reset(),
    };
  }
  private reset() {
    this.armed = false;
    this.zone.hidden = true;
    this.zone.classList.remove("is-armed");
  }
  private resolve(remove: boolean) {
    const pending = this.pending;
    this.pending = null;
    if (
      pending &&
      pending.generation === this.session.generation &&
      pending.revision === this.session.revision
    ) {
      // Roll back the move before deletion: one Undo restores the original pose.
      this.session.cancel();
      if (remove) {
        this.session.select(pending.ids);
        this.session.removeSelected();
      }
    }
    if (this.dialog.open) this.dialog.close();
    this.changed();
  }
}
